import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { readAuthoredItem } from "../../../api/content/authoring/processAuthoringApi";
import type { ArtifactContent } from "../../../api/content/authoring/processContentDecoders";
import { useLoad } from "../../../shared/loading/useLoad";
import AppButton from "../../../ui/buttons/AppButton";
import { FORM_COMPACT_CONTROL_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import type { CommitBuilder } from "../../changeSets/changeSetChoice";
import { editIntent, useHerePath, useThreadPreparation } from "../../threads/prepare";
import AddToChangeSet from "../draft/AddToChangeSet";
import { itemsOfKind, type AuthoringOptions } from "../draft/authoringOptions";
import DefinitionPanel from "../draft/DefinitionPanel";
import FieldProblems from "../draft/FieldProblems";
import { itemEditAt, replacingTargetOf, type ItemTarget } from "../draft/itemEdits";
import { useDefinitionCheck } from "../draft/useDefinitionCheck";
import { useUnsavedDraft } from "../draft/useUnsavedDraft";
import SimilarItemsPanel from "../similar/SimilarItemsPanel";
import MarkdownField from "../../../ui/forms/MarkdownField";
import Section from "../../../ui/forms/Section";
import { annotationSetContent } from "../../../api/content/documentAnnotationsApi";
import EditableAnnotationSet from "../../annotation/EditableAnnotationSet";
import { annotationSetOf, CHECKLIST_SCHEMA, checklistContent, checklistHints, checklistRows, editorOf, GLOSSARY_SCHEMA, glossaryContent, glossaryHints, glossaryRows, MARKDOWN_SCHEMA } from "./artifactTables";
import ChecklistTable from "./ChecklistTable";
import GlossaryTable from "./GlossaryTable";
import JsonContentEditor from "./JsonContentEditor";
import RowHints from "./RowHints";
import { CONTROL_ROW_TEXT_CLASS } from "../shared/authoringStyles";
import Field from "../../../ui/forms/Field";
import OptionLabel from "../../../ui/forms/OptionLabel";

export interface ArtifactBase {
  readonly reference: string;
  readonly itemId: string;
  readonly content: ArtifactContent;
}

const NEW_ARTIFACT: ArtifactContent = { kind: "artifact", name: "", media_type: "application/json", schema: GLOSSARY_SCHEMA, derived_from: [], content: { terms: [] } };

/** An empty description is none: the review's shapes say what a missing one means (PS-1), not a refused save. */
function withDescription(artifact: ArtifactContent, description: string): ArtifactContent {
  const { description: _previous, ...rest } = artifact;
  return description === "" ? rest : { ...rest, description };
}

/** The empty content of a schema's editor. */
function emptyContent(mediaType: ArtifactContent["media_type"], schema: string): unknown {
  switch (editorOf(mediaType, schema)) {
    case "markdown":
      return "";
    case "glossary":
      return { terms: [] };
    case "checklist":
      return { items: [] };
    case "annotations":
    case "json":
      return {};
  }
}

/**
 * The artifact form (AU-5, mockup `new-artifact.html`): name, media type, schema, and an
 * editor for the content by schema. Made by hand, so `derived_from` stays as it was; the
 * server sets it for artifacts a step proposes.
 */
export default function ArtifactForm({ projectId, options, base }: { projectId: string; options: AuthoringOptions; base: ArtifactBase | null }) {
  const { t } = useTranslation();
  const [artifact, setArtifact] = useState<ArtifactContent>(() => base?.content ?? NEW_ARTIFACT);
  const [target] = useState<ItemTarget>(() => replacingTargetOf(base));
  const preparation = useThreadPreparation();
  const here = useHerePath();
  const labelLists = itemsOfKind(options, "label_list");
  const [labelList, setLabelList] = useState(labelLists[0]?.reference ?? "");
  const loadLabels = useCallback((signal: AbortSignal) => (labelList === "" ? Promise.resolve(null) : readAuthoredItem(labelList, signal)), [labelList]);
  const labels = useLoad(loadLabels).state;
  const labelKeys = labels.status === "loaded" && labels.value?.content?.kind === "label_list" ? labels.value.content.definition.labels.map((label) => label.key) : null;

  const { connectSaver, markSaved } = useUnsavedDraft(artifact);
  const check = useDefinitionCheck(projectId, artifact, base === null ? null : target.itemId);
  const editor = editorOf(artifact.media_type, artifact.schema);
  const glossary = editor === "glossary" ? glossaryRows(artifact.content) : null;
  const checklist = editor === "checklist" ? checklistRows(artifact.content) : null;
  const annotationSet = editor === "annotations" ? annotationSetOf(artifact.content) : null;
  const hints = useMemo(() => (glossary !== null ? glossaryHints(glossary, labelKeys) : checklist !== null ? checklistHints(checklist, labelKeys) : []), [glossary, checklist, labelKeys]);
  const commits = useCallback((): readonly CommitBuilder[] => [async (head) => [await itemEditAt(projectId, head, target, artifact)]], [projectId, target, artifact]);

  const setSchema = (mediaType: ArtifactContent["media_type"], schema: string) => {
    setArtifact({ ...artifact, media_type: mediaType, schema, content: editorOf(mediaType, schema) === editor ? artifact.content : emptyContent(mediaType, schema) });
  };

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <div className="space-y-4">
        <div className="flex justify-end">
          <AppButton variant="ghost" size="sm" onClick={() => {
            preparation.prepare(base === null
              ? { kind: "author", projectId, returnTo: here, itemKind: "artifact", draft: artifact, name: artifact.name }
              : editIntent(projectId, { itemKind: "artifact", name: artifact.name, reference: base.reference }, here));
          }}>{t("authoring.artifact.withAgent")}</AppButton>
        </div>
        <Section title={t("authoring.artifact.about")}>
          <Field label={t("authoring.artifact.name")}>
            {(control) => (
              <input {...control} className={FORM_COMPACT_CONTROL_CLASS} value={artifact.name} maxLength={200} onChange={(event) => { setArtifact({ ...artifact, name: event.target.value }); }} />
            )}
          </Field>
          <FieldProblems check={check} path="name" />
          <Field label={t("authoring.artifact.description")}>
            {(control) => (
              <input {...control} className={FORM_COMPACT_CONTROL_CLASS} value={artifact.description ?? ""} onChange={(event) => { setArtifact(withDescription(artifact, event.target.value)); }} />
            )}
          </Field>
          <FieldProblems check={check} path="description" />
          <div className="flex flex-wrap items-center gap-4 text-sm" role="radiogroup" aria-label={t("authoring.artifact.mediaType")}>
            <span className="app-label">{t("authoring.artifact.mediaType")}</span>
            <OptionLabel>
              <input type="radio" disabled={base !== null} checked={artifact.media_type === "application/json"} onChange={() => { setSchema("application/json", GLOSSARY_SCHEMA); }} />
              JSON
            </OptionLabel>
            <OptionLabel>
              <input type="radio" disabled={base !== null} checked={artifact.media_type === "text/markdown"} onChange={() => { setSchema("text/markdown", MARKDOWN_SCHEMA); }} />
              Markdown
            </OptionLabel>
          </div>
          {artifact.media_type === "application/json" ? (
            <SchemaChoice schema={artifact.schema} locked={base !== null} onChange={(schema) => { setSchema("application/json", schema); }} />
          ) : null}
          <FieldProblems check={check} path="schema" />
          <p className="app-help-text text-xs">{t(`authoring.artifact.editorHints.${editor}`)}</p>
        </Section>
        <Section title={t("authoring.artifact.content")}>
          {glossary !== null || checklist !== null ? (
            <Field layout="inline" label={t("authoring.artifact.labelsFrom")} className="text-xs">
              {(control) => (
                <select {...control} className={`${FORM_SELECT_CLASS} w-auto`} value={labelList} onChange={(event) => { setLabelList(event.target.value); }}>
                  <option value="">{t("authoring.artifact.noLabelList")}</option>
                  {labelLists.map((list) => <option key={list.reference} value={list.reference}>{list.name}</option>)}
                </select>
              )}
            </Field>
          ) : null}
          {editor === "markdown" ? (
            <MarkdownField label={t("authoring.artifact.content")} labelHidden value={typeof artifact.content === "string" ? artifact.content : ""} rows={16} onChange={(content) => { setArtifact({ ...artifact, content }); }} />
          ) : null}
          {glossary !== null ? <GlossaryTable rows={glossary} labelKeys={labelKeys} onChange={(rows) => { setArtifact({ ...artifact, content: glossaryContent(rows) }); }} /> : null}
          {checklist !== null ? <ChecklistTable rows={checklist} onChange={(rows) => { setArtifact({ ...artifact, content: checklistContent(rows) }); }} /> : null}
          {annotationSet !== null ? <EditableAnnotationSet set={annotationSet} onChange={(set) => { setArtifact({ ...artifact, content: annotationSetContent(set) }); }} /> : null}
          {editor === "json" || (editor === "glossary" && glossary === null) || (editor === "checklist" && checklist === null) || (editor === "annotations" && annotationSet === null) ? (
            <JsonContentEditor value={artifact.content} onChange={(content) => { setArtifact({ ...artifact, content }); }} />
          ) : null}
          <RowHints hints={hints} />
          <FieldProblems check={check} path="content" />
        </Section>
      </div>
      <div className="space-y-4">
        <SimilarItemsPanel projectId={projectId} kind="artifact" name={artifact.name} description={artifact.description ?? ""} excludeItemId={base?.itemId} />
        <DefinitionPanel content={artifact} base={base?.content ?? null} check={check} />
        <AddToChangeSet
          ref={connectSaver}
          projectId={projectId}
          defaultTitle={t(base === null ? "authoring.artifact.addTitle" : "authoring.artifact.editTitle", { name: artifact.name.trim() || t("authoring.artifact.unnamed") })}
          commits={commits}
          blockedBy={artifact.name.trim() === "" ? t("authoring.artifact.blockedName") : null}
          onSaved={markSaved}
          // How items, versions and reviews relate is Help's (OW-12); only what changes for others stays.
          notice={base === null ? undefined : <p className="app-help-text text-xs">{t("authoring.artifact.versionNotice")}</p>}
        />
      </div>
    </div>
  );
}

const KNOWN_SCHEMAS = [GLOSSARY_SCHEMA, CHECKLIST_SCHEMA] as const;

function SchemaChoice({ schema, locked, onChange }: { schema: string; locked: boolean; onChange: (schema: string) => void }) {
  const { t } = useTranslation();
  const known = KNOWN_SCHEMAS.some((candidate) => candidate === schema);
  return (
    <div className={CONTROL_ROW_TEXT_CLASS}>
      <Field label={t("authoring.artifact.schema")} className="flex items-center gap-2">
        {(control) => (
          <select {...control} className={`${FORM_SELECT_CLASS} w-auto`} disabled={locked} value={known ? schema : ""} onChange={(event) => { onChange(event.target.value === "" ? "" : event.target.value); }}>
            {KNOWN_SCHEMAS.map((candidate) => <option key={candidate} value={candidate}>{candidate}</option>)}
            <option value="">{t("authoring.artifact.otherSchema")}</option>
          </select>
        )}
      </Field>
      {known ? null : (
        <input className={`${FORM_COMPACT_CONTROL_CLASS} w-56 font-mono`} disabled={locked} aria-label={t("authoring.artifact.schemaName")} placeholder="my-schema/1" value={schema} onChange={(event) => { onChange(event.target.value); }} />
      )}
    </div>
  );
}
